import { SoftwareTalent } from '../types';
import { getTalents, DomainType } from '../services/api';

const CSV_HEADERS = [
  'ID',
  'Name',
  'Domain',
  'Field',
  'Google Scholar URL',
  'Resume',
  'Title',
  'Country',
  'Year Of Experience',
  'Skills',
  'Top School',
  'Profile Link',
  'Hourly Rate',
  'Short Summary',
  'LinkedIn URL',
  'LinkedIn Verifications',
];

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s === '') return '';
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function getSkills(t: SoftwareTalent): string {
  if (Array.isArray(t.tSkills) && t.tSkills.length > 0) {
    return t.tSkills.filter(Boolean).join('; ');
  }
  if (Array.isArray(t.skills) && t.skills.length > 0) {
    return t.skills
      .map((s) => (typeof s === 'string' ? s : s?.name || ''))
      .filter(Boolean)
      .join('; ');
  }
  return '';
}

function getTopSchool(t: SoftwareTalent): string {
  const fromPedigree = t.pedigreeSignals?.topSchool;
  if (fromPedigree) return fromPedigree;
  const fromBats = t.bayAreaTalentSignals?.evidence?.topSchools?.[0]?.name;
  return fromBats || '';
}

function resumeLink(id: number): string {
  return `https://matching.turing.com/developer/${id}/resume`;
}

function customerProfileLink(id: number, domain: string | undefined): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const tid = typeof window !== 'undefined' ? window.localStorage.getItem('tid') : null;
  const params = new URLSearchParams();
  if (domain) params.set('domain', domain);
  if (tid) params.set('tid', tid);
  const qs = params.toString();
  return `${origin}/candidate/${id}${qs ? `?${qs}` : ''}`;
}

function formatLinkedInVerifications(t: SoftwareTalent): string {
  const v = t.linkedInVerification;
  if (!v) return '';
  const parts: string[] = [];
  if (v.joinedDate) parts.push(`Joined ${v.joinedDate}`);
  if ((v.Identity?.length ?? 0) > 0) parts.push('Identity Verified');
  if ((v.Workplace?.length ?? 0) > 0) parts.push('Workplace Verified');
  if ((v.EducationalInstitution?.length ?? 0) > 0) parts.push('Education Verified');
  return parts.join('; ');
}

function talentToRow(t: SoftwareTalent): string {
  const row = [
    t.id,
    t.name,
    t.domain ?? '',
    t.field ?? '',
    t.scholarVerify?.url ?? '',
    resumeLink(t.id),
    t.title ?? '',
    t.country ?? '',
    t.yearOfExperience ?? '',
    getSkills(t),
    getTopSchool(t),
    customerProfileLink(t.id, t.domain),
    t.hourlyRate ?? '',
    t.tShortSummary ?? '',
    t.linkedInProfile ?? '',
    formatLinkedInVerifications(t),
  ];
  return row.map(csvEscape).join(',');
}

async function fetchAllSavedForDomain(domain: DomainType): Promise<SoftwareTalent[]> {
  const pageSize = 200;
  const collected: SoftwareTalent[] = [];
  let page = 1;
  while (true) {
    const res = await getTalents('SAVED', domain, page, pageSize);
    if (!res.success || !Array.isArray(res.engineers)) break;
    const batch = res.engineers
      .filter((e) => !e.isDiscarded) // saved tab excludes discarded
      .map((e) => ({ ...e, domain }));
    collected.push(...batch);
    const total = res.pagination?.total ?? collected.length;
    if (res.engineers.length === 0 || collected.length >= total) break;
    page += 1;
    if (page > 200) break; // hard safety cap (40,000 profiles)
  }
  return collected;
}

function triggerDownload(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportSavedProfilesCsv(domains: DomainType[]): Promise<number> {
  const results = await Promise.all(domains.map(fetchAllSavedForDomain));
  const all = results.flat();

  const lines = [CSV_HEADERS.join(','), ...all.map(talentToRow)];
  const csv = lines.join('\n') + '\n';

  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  triggerDownload(csv, `saved-profiles-${ts}.csv`);

  return all.length;
}
